/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';

declare module '@emotion/react' {
  export interface Theme {
    palette: {
      // Navigation & Layout
      navLinkColor: string;
      layoutBorderBottomColor: string;

      // Background Colors
      tertiaryBgColor: string;
      bgWhiteColor: string;
      pageBgColor: string;
      brandBackdrop: string;
      lightGrayBg: string;
      veryLightGrayBg: string;
      blueGrayBg: string;

      // Primary & Accent Colors
      logiratePrimary: string;
      logiratePrimaryLight: string;
      blueColor: string;
      greenColor: string;

      // Form Highlight Colors
      logiFormBorderHighlight: string;
      logiFormBackgroundHighlight: string;

      // Red/Error Colors
      redFontColor: string;
      redHoverFontColor: string;
      errorRed: string;

      // Border Colors
      darkBorderColor: string;
      darkestBorderColor: string;
      borderColor: string;
      lightBorder: string;
      mediumBorder: string;
      grayBorder: string;

      // Text Colors
      blackColor: string;
      logirateBlack: string;
      darkTextColor: string;
      primaryTextColor: string;
      secondaryTextColor: string;
      tertiaryTextColor: string;
      grayTextColor: string;
      lightGrayText: string;
      mediumGrayText: string;
      formFooterText: string;
      formDescriptionText: string;

      // Highlight & Neutral Colors
      highlightColor: string;
      neutralGray: string;
      lightNeutralGray: string;

      // Warning Colors
      warningBgColor: string;
      warningTextColor: string;

      // Overlay & Shadow Colors
      modalBackdrop: string;
      lightOverlay: string;
      boxShadowColor: string;
      lightBoxShadowColor: string;
    };
  }
}

export const theme: Theme = {
  palette: {
    // Navigation & Layout
    navLinkColor: '#1F2B34',
    layoutBorderBottomColor: '#e0e0e0',

    // Background Colors
    tertiaryBgColor: '#FAFBFB',
    bgWhiteColor: '#FFFFFF',
    pageBgColor: '#F7F9FE',
    brandBackdrop: '#E7EFFB',
    lightGrayBg: '#f8f8f8',
    veryLightGrayBg: '#f3f4f6',
    blueGrayBg: '#f7f9fc',

    // Primary & Accent Colors
    logiratePrimary: 'rgba(59, 124, 244, 0.5)',
    logiratePrimaryLight: 'rgba(59, 124, 244, 0.1)',
    blueColor: '#5b91f5',
    greenColor: '#00b73f',

    // Form Highlight Colors
    logiFormBorderHighlight: 'rgba(244, 111, 59, 0.5)',
    logiFormBackgroundHighlight: 'rgba(59, 124, 244, 0.05)',

    // Red/Error Colors
    redFontColor: '#DA0E29',
    redHoverFontColor: '#c4031d',
    errorRed: '#ff0500',

    // Border Colors
    darkBorderColor: '#D2D5DA',
    darkestBorderColor: '#9CA3AF',
    borderColor: '#E5E7EB',
    lightBorder: '#f4f4f5',
    mediumBorder: '#dddddd',
    grayBorder: '#d5d5dd',

    // Text Colors
    blackColor: '#000000',
    logirateBlack: '#232222',
    darkTextColor: '#070707',
    primaryTextColor: '#1f2937',
    secondaryTextColor: '#374151',
    tertiaryTextColor: '#374151',
    grayTextColor: '#6b7280',
    lightGrayText: '#867c79',

    mediumGrayText: '#71748c',
    formFooterText: '#81899E',
    formDescriptionText: '#6B7280',

    // Highlight & Neutral Colors
    highlightColor: '#F6F6F6',
    neutralGray: '#41415a',
    lightNeutralGray: '#f1f1f4',

    // Warning Colors
    warningBgColor: 'rgba(255, 113, 113, 0.1)',
    warningTextColor: '#FF7171',

    // Overlay & Shadow Colors
    modalBackdrop: 'rgba(0, 0, 0, 0.5)',
    lightOverlay: 'rgba(0, 0, 0, 0.08)',
    boxShadowColor: 'rgba(0, 0, 0, 0.05)',
    lightBoxShadowColor: 'rgba(0, 0, 0, 0.15)',
  },
};

const GlobalStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  * {
    margin: 0;
    font-size: 1em;
  }
  html,
  body {
    height: 100%;
  }
  body {
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    font-family: 'Manrope', 'Work Sans', sans-serif;
    font-weight: 400;
    color: black;
  }
  img,
  picture,
  video,
  canvas,
  svg {
    display: block;
    max-width: 100%;
  }
  input,
  button,
  textarea,
  select {
    font: inherit;
  }
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    overflow-wrap: break-word;
  }
  #root,
  #__next {
    isolation: isolate;
  }
  /* Recharts 3 makes the chart surface focusable; hide the click focus ring */
  .recharts-wrapper,
  .recharts-wrapper *:focus,
  .recharts-surface:focus,
  .recharts-sector:focus,
  .recharts-layer:focus {
    outline: none;
  }
`;

export default GlobalStyles;
